import { Divider, Grid, TooltipProps, Typography } from "@mui/material";
import { useEffect, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog, { DialogSectionTitle, IDefaultDialogProps } from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import DefaultConstants from "../../../../data/Constants";
import FormHelper from "../../../../helpers/FormHelper";
import { IRoomBlocking } from "../../../../interfaces/IPartnerPms";
import DateTimeHelper from "../../../../helpers/DateTimeHelper";
import PartnerPmsService from "../../../../services/PartnerPmsService";
import useState from "../../../../hooks/useState";
import { RegistrationStatusData } from "../../../../data/Enums";
import FormOption from "../../../../components/FormOption";
import { Check, Close, Lock, Today } from "@mui/icons-material";
import useDialog from "../../../../hooks/useDialog";
import { IMuiIcon } from "../../../../interfaces/IMaterial";



const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];



interface IViewDialogProps extends IAddEditDialogProps<IRoomBlocking> {
    hasPermissionToRemove: boolean;
}


interface IStateValues {
    dates: number[];
}

const ViewDialog: React.FunctionComponent<IViewDialogProps> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {},
    hasPermissionToRemove
}) => {
    const { translate } = useLanguageContext();

    const state = useState<IStateValues>({
        dates: []
    });

    const removeDialog = useDialog();


    useEffect(() => {
        if (open) {
            removeDialog.handleClose();
            state.update('dates', []);
        }
        // eslint-disable-next-line
    }, [open]);


    const dialogActions = useMemo(() => {
        const response = [<ActionButton action='close' onClick={handleClose} />];

        if (hasPermissionToRemove && state.values.dates.length > 0) {
            response.push(<ActionButton action='remove' onClick={removeDialog.handleOpen} />);
        }

        return response;
        // eslint-disable-next-line
    }, [translate, hasPermissionToRemove, state.values.dates.length]);


    const handleChangeDates = useCallback((id: number) => {
        const datesAux = state.values.dates;
            
        if (datesAux.includes(id)) {
            const index = datesAux.findIndex(r => r === id);
            datesAux.splice(index, 1);
        } else {
            datesAux.push(id);
        }
    
        state.update('dates', datesAux);
        // eslint-disable-next-line
    }, [state.values.dates]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={data?.roomDescription ?? '-'}
            maxWidth='sm'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            className='dialogactions-spacebetween'
            afterForm={<>
                {
                    hasPermissionToRemove && <RemoveDialog 
                        open={removeDialog.open}
                        handleClose={() => removeDialog.handleClose()}
                        datesToRemove={state.values.dates}
                        callbackWhenSubmit={() => {
                            handleListRefresh();
                            handleClose();
                        }}
                    />
                }
            </>}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing}>
                <Grid item md={6} xs={12}>
                    <Typography variant='caption' className='fontsize-10 text-color-2'>
                        {translate('startDate')}
                    </Typography>
            
                    <Typography variant='body2'>
                        {data?.startDate ? DateTimeHelper.Format(data.startDate) : '-'}
                    </Typography>
                </Grid>

                <Grid item md={6} xs={12}>
                    <Typography variant='caption' className='fontsize-10 text-color-2'>
                        {translate('endDate')}
                    </Typography>
            
                    <Typography variant='body2'>
                        {data?.endDate ? DateTimeHelper.Format(data.endDate) : '-'}
                    </Typography>
                </Grid>

                <Grid item md={6} xs={12}>
                    <Typography variant='caption' className='fontsize-10 text-color-2'>
                        {translate('blockingType')}
                    </Typography>
            
                    <Typography variant='body2'>
                        {data?.typeDescription ?? '-'}
                    </Typography>
                </Grid>
            
                <Grid item md={6} xs={12}>
                    <Typography variant='caption' className='fontsize-10 text-color-2'>
                        {translate('situation')}
                    </Typography>
            
                    <Typography variant='body2'>
                        {translate(RegistrationStatusData[data?.registrationStatusId ?? 0].name)}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant='caption' className='fontsize-10 text-color-2'>
                        {translate('description')}
                    </Typography>
            
                    <Typography variant='body2'>
                        {data?.description ?? '-'}
                    </Typography>
                </Grid>

                <Grid item xs={12} marginY={2}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={translate('dates')} />
                </Grid>

                <Grid item xs={12}>
                    <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing} marginTop={-1}>
                        {
                            (data?.dates ?? []).map((d, index) => {
                                const id = +d.id;
                                const selected = state.values.dates.includes(id);

                                const statusProps = { 
                                    selectedIcon: Check, 
                                    disabledIcon: undefined as IMuiIcon | undefined, 
                                    tooltip: {
                                        title: '',
                                        placement: 'bottom-end'
                                    } as TooltipProps
                                };

                                if (hasPermissionToRemove) {
                                    if (d.removed) {
                                        statusProps.disabledIcon = Close;
                                        statusProps.tooltip.title = translate('_roomBlockingDialogRemovedDateDescription')
                                            .replace('{date}', DateTimeHelper.Format(d.removedAt) ?? '-')
                                            .replace('{time}', DateTimeHelper.Format(d.removedAt, 'HH:mm') ?? '-')
                                            .replace('{user}', d.removedBy ?? '-');
                                    } 
                                    
                                    else if (d.pastDate) {
                                        statusProps.disabledIcon = Lock;
                                        statusProps.tooltip.title = translate('_roomBlockingDialogPastDateDescription');
                                    }
                                }

                                return <Grid item sm={4} xs={6} key={id}>
                                    <FormOption
                                        title={DateTimeHelper.Format(d.date) ?? ''}
                                        type='checkbox'
                                        name={`roomBlocking[${index}]`}
                                        value={id}
                                        selected={selected}
                                        disabled={!hasPermissionToRemove || !d.selectable}
                                        onClick={() => handleChangeDates(id)}
                                        Icon={Today}
                                        orientation='vertical'
                                        subtitle={translate(weekDays[new Date(d.date).getDay()])}
                                        statusProps={statusProps}
                                    />
                                </Grid>;
                            })
                        }
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default ViewDialog;




interface IRemoveDialogProps extends IDefaultDialogProps {
    datesToRemove: number[];
    callbackWhenSubmit: () => void;
}

const RemoveDialog: React.FunctionComponent<IRemoveDialogProps> = ({
    open,
    handleClose,
    datesToRemove,
    callbackWhenSubmit
}) => {
    const { translate } = useLanguageContext();

    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='no' onClick={handleClose} />,
            <ActionButton action='yes' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (datesToRemove.length === 0) {
            return;
        }

        let formData = FormHelper.Create(event);

        datesToRemove.forEach((d, index) => {
            formData = FormHelper.Add(formData, `id[${index}]`, d);
        });

        PartnerPmsService.RemoveRoomBlocking(formData).then(() => {
            handleClose();
            callbackWhenSubmit();
        });
        // eslint-disable-next-line
    }, [datesToRemove]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate('removeBlocking')}
            dialogActions={dialogActions}
            className='dialogactions-spacebetween overflow-inherit'
            onSubmit={handleSubmit}
            absolute={true}
            dividers={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item xs={12} marginY={2}>
                    <Typography variant='body1'>
                        {translate(datesToRemove.length === 1 ? '_roomBlockingDialogRemoveDescriptionSingleDate' : '_roomBlockingDialogRemoveDescriptionMultipleDates')}
                    </Typography>
                </Grid>
            </Grid>
        </Dialog>
    );
}